import { useCallback, useEffect, useRef } from 'react';
import type { ForcedArtifact, QuestionAnswerBody } from '@ideoz/core';
import { useConversation } from '../context/ConversationContext';

/**
 * Owns the message-turn lifecycle for the conversation workspace (extracted
 * verbatim from `ConversationModule` in Phase 2, slice 5): the on-mount initial
 * message, submitting a prompt, answering an interactive question set, feedback,
 * ensuring a conversation id for attachments, and stream cancellation.
 *
 * Reads `facade`/`workbench`/`urlConversationId`/`isWeb` from `useConversation()`
 * (same object identities → moved dep arrays unchanged). `isReplyStillActive`
 * (shared with the prototype-stream hook) and `resetBuildStreamLatch` (from the
 * prototype-stream hook) come in as params.
 */
interface UseConversationTurnsParams {
  isReplyStillActive: (
    submittedConversationId: string | null | undefined,
    newConversationId?: string
  ) => boolean;
  resetBuildStreamLatch: () => void;
}

export function useConversationTurns({
  isReplyStillActive,
  resetBuildStreamLatch,
}: UseConversationTurnsParams) {
  const { facade, workbench, urlConversationId, isWeb } = useConversation();
  const {
    currentConversationId,
    messages,
    isActive,
    session_id,
    init_message,
    initialAttachmentIds,
    skip_analysis,
    llmFamily,
    isStreaming,
  } = facade.state;
  const {
    isStreamingPrototype = false,
    isWorkbenchBuildLive = false,
    onCancelWorkbenchStream,
  } = workbench;

  const hasInitializedRef = useRef(false);

  /**
   * Initialize conversation on mount (matching old implementation)
   * When isActive and no messages, submit the initial message and start streaming
   * Use ref to prevent double initialization
   */
  useEffect(() => {
    if (isActive && messages.length === 0 && !hasInitializedRef.current) {
      hasInitializedRef.current = true;

      // Submit initial message with callback for header processing
      if (!currentConversationId && session_id) {
        facade.actions.submitMessage(
          init_message || '',
          initialAttachmentIds,
          (newConversationId, headers) => {
            // Process headers when streaming completes — but only if the user hasn't
            // since navigated to a different conversation (see isReplyStillActive).
            if (headers && isReplyStillActive(currentConversationId, newConversationId)) {
              facade.headers.processHeaders(
                headers,
                currentConversationId,
                newConversationId,
                undefined,
                urlConversationId,
                facade.state.conversationId || undefined
              );
            }
          }
        );
      }
    }
  }, [
    isActive,
    messages.length,
    init_message,
    currentConversationId,
    session_id,
    initialAttachmentIds,
    skip_analysis,
    llmFamily,
    facade.actions,
    facade.streaming,
    isWeb,
    isReplyStillActive,
  ]);

  /**
   * Handle message submission
   */
  const handleSubmit = useCallback(
    (
      text: string,
      attachmentIds: string[] = [],
      llmFamilyParam?: string,
      ensuredConversationId?: string,
      forcedArtifact?: ForcedArtifact
    ) => {
      // Validate input
      if (!text.trim() && (!attachmentIds || attachmentIds.length === 0)) {
        console.log('🎯 Validation failed, returning early');
        return;
      }

      // Use provided llmFamily or fall back to state llmFamily
      const selectedLlmFamily = llmFamilyParam || llmFamily;

      // Reset header states
      facade.headers.setIsClarification(false);
      facade.headers.setIsReflection(false);
      facade.headers.setIsRepeated(false);
      facade.headers.setIsPrototyper(false);
      facade.headers.setIsReadyToCreate(false);
      facade.streaming.clearClarification();
      resetBuildStreamLatch();
      // Submit message with callback to process headers
      facade.actions.submitMessage(
        text,
        attachmentIds,
        (newConversationId, headers) => {
          // Process headers when streaming completes — but only if the user hasn't since
          // navigated to a different conversation (see isReplyStillActive).
          if (headers && isReplyStillActive(currentConversationId, newConversationId)) {
            facade.headers.processHeaders(
              headers,
              currentConversationId,
              newConversationId,
              undefined,
              urlConversationId,
              facade.state.conversationId || undefined
            );
          }
        },
        undefined,
        ensuredConversationId,
        forcedArtifact
      );
    },
    [
      facade.actions,
      facade.headers,
      facade.streaming,
      currentConversationId,
      session_id,
      isWeb,
      llmFamily,
      isReplyStillActive,
    ]
  );

  /**
   * Answer an interactive question set. Streams the answer (ids, no prose) on
   * the conversation that asked; the card clears once the backend takes the
   * answer, and stays put with the reason if it doesn't.
   */
  const handleAnswerSubmit = useCallback(
    (answer: QuestionAnswerBody) => {
      facade.headers.setIsClarification(false);
      facade.streaming.clearClarification();
      facade.streaming.submitAnswer(
        answer,
        currentConversationId ?? undefined,
        (newConversationId, headers) => {
          // Only apply if the user hasn't since navigated to a different conversation.
          if (headers && isReplyStillActive(currentConversationId, newConversationId)) {
            facade.headers.processHeaders(
              headers,
              currentConversationId,
              newConversationId,
              undefined,
              urlConversationId,
              facade.state.conversationId || undefined
            );
          }
        }
      );
    },
    [
      facade.headers,
      facade.streaming,
      facade.state,
      currentConversationId,
      urlConversationId,
      isReplyStillActive,
    ]
  );

  /**
   * Handle streaming cancellation
   */
  const handleCancel = useCallback(() => {
    if (isStreaming) {
      facade.actions.cancelStreaming();
    }
    if (isStreamingPrototype && isWorkbenchBuildLive) {
      onCancelWorkbenchStream?.();
    }
    facade.prototyper.cancelPrototype();
  }, [
    facade.actions,
    facade.prototyper,
    isStreaming,
    isStreamingPrototype,
    isWorkbenchBuildLive,
    onCancelWorkbenchStream,
  ]);

  const handleEnsureConversationId = useCallback(async () => {
    if (currentConversationId) {
      return currentConversationId;
    }
    return facade.actions.ensureConversationForAttachments();
  }, [currentConversationId, facade.actions]);

  /**
   * Handle feedback (like/dislike)
   */
  const handleFeedback = useCallback(
    async (type: 'like' | 'dislike', messageId?: string | null, projectId?: string | null) => {
      if (!messageId) return;
      await facade.actions.sendFeedback(type, messageId, projectId);
    },
    [facade.actions]
  );

  return {
    handleSubmit,
    handleAnswerSubmit,
    handleCancel,
    handleEnsureConversationId,
    handleFeedback,
  };
}

export type ConversationTurns = ReturnType<typeof useConversationTurns>;
